/**
 * Spec 156 (CC-53907) — what the Email page's Compose tab may say about a send, read from
 * cb-api's `POST /email/send` answer (`cb-api/src/domains/email/compose.js`).
 *
 * ## Never "sent"
 *
 * The most a 200 can establish is that Centerbase ACCEPTED the email. CB returns a real
 * email id even when its own O365 send failed — Graph threw, the basic-auth fallback
 * failed too, `cbmail.Send()` returned false and `ServerFiles.cs:476-480` passed that
 * only to logging. Report delivery reached the same ceiling (spec 137 decision 10), so
 * the words are its words: `RECIPIENT_ACCEPTED_LINE` in
 * `components/reports/queue/deliveryOutcome.js`, imported rather than spelled again here,
 * so there is one spelling (a test reads this file to prove it).
 *
 * ## An answer we cannot read is not a refusal
 *
 * A request can end with no answer (axios's 30s timeout, a dropped connection) or with an
 * answer that carries no sentence (an edge HTML 502/504/524). In both the request may
 * already have reached CB's mailer — the worker bounds each SendEmail POST at 25s, not
 * the cookie exchange in front of it — so the page says the email may already have gone
 * out. It never shows axios's own words ("timeout of 30000ms exceeded").
 */

import { RECIPIENT_ACCEPTED_LINE } from '@components/reports/queue/deliveryOutcome'

export const ACCEPTED_TITLE = RECIPIENT_ACCEPTED_LINE

export const ACCEPTED_NOTE =
  'Centerbase doesn’t confirm delivery, so this isn’t proof the email arrived. Until you start another email, you can restore this one to the form.'

export const UNKNOWN_OUTCOME =
  'We couldn’t tell whether this email was sent, and it may already have gone out. Check with the recipient before sending it again.'

/**
 * The outcome of a send the server answered with a 2xx.
 * @returns {{kind: 'accepted'} | {kind: 'problem', message: string}}
 */
export function outcomeOfAnswer(res) {
  const data = res?.data
  if (data?.status === 'accepted') return { kind: 'accepted' }
  // Only `unconfirmed` carries a sentence of the server's own; any other 200 is unread.
  if (data?.status === 'unconfirmed' && typeof data.message === 'string' && data.message.trim()) {
    return { kind: 'problem', message: data.message }
  }
  return { kind: 'problem', message: UNKNOWN_OUTCOME }
}

/**
 * The outcome of a send that failed. cb-api writes a sentence for every answer it gives
 * — including whether anything was sent — so that sentence is shown as it is. Anything
 * without one is the unknown outcome, never axios's message.
 * @returns {{kind: 'problem', message: string}}
 */
export function outcomeOfFailure(err) {
  const said = err?.response?.data?.error
  if (err?.response && typeof said === 'string' && said.trim()) return { kind: 'problem', message: said }
  return { kind: 'problem', message: UNKNOWN_OUTCOME }
}
